import dayjs from 'dayjs'

import { cn } from '@/app/utils'
import { getMskNow } from '@/utils/datetime'

import { slicedMonthNames } from './MonthPicker.constants'
import { DateRange } from './MonthRangePicker.types'

type Props = {
  range?: DateRange
  displayRange?: DateRange
  setRange: (range?: DateRange) => void
  monthYear: Date
  selectFullMonthRange?: boolean
  cellClassName?: string
  selectedMonth?: Date
  disabled?: {
    before?: Date
    after?: Date
  }
}

export const MonthPicker = ({
  range = { from: undefined, to: undefined },
  displayRange,
  setRange,
  monthYear,
  cellClassName,
  selectedMonth,
  selectFullMonthRange = false,
  disabled
}: Props) => {
  const highlightRange = displayRange ?? range

  const handleMonthClick = (monthIndex: number) => {
    const year = monthYear.getFullYear()
    const clickedDate = dayjs().year(year).month(monthIndex).startOf('month')
    const { from, to } = range

    // если нажать на уже выбранную дату, то диапазон сбрасывается
    if (
      (from && clickedDate.isSame(from, 'month')) ||
      (to && clickedDate.isSame(to, 'month'))
    ) {
      setRange({ from: undefined, to: undefined })
      return
    }

    // если ничего не выбрано
    if (!from && !to) {
      setRange({
        from: clickedDate.toDate(),
        to: selectFullMonthRange
          ? clickedDate.endOf('month').toDate()
          : undefined
      })
      return
    }

    const handleFullMonthSelection = (
      clickedDate: dayjs.Dayjs,
      from?: Date,
      to?: Date
    ) => {
      if (!from && !to) {
        setRange({
          from: clickedDate.toDate(),
          to: clickedDate.endOf('month').toDate()
        })
        return
      }

      if (from && to) {
        const updatedFrom = clickedDate.isBefore(dayjs(from))
          ? clickedDate
          : dayjs(from)
        const updatedTo = clickedDate.endOf('month').isAfter(dayjs(to))
          ? clickedDate.endOf('month')
          : dayjs(to)
        setRange({ from: updatedFrom.toDate(), to: updatedTo.toDate() })
      }
    }

    if (selectFullMonthRange) {
      // для выбора по датам в случае клика на месяц конечной датой устанавливается последнее число месяца
      handleFullMonthSelection(clickedDate, from, to)
      return
    }

    if (from && !to) {
      if (clickedDate.isBefore(from)) {
        setRange({ from: clickedDate.toDate(), to: from })
      } else {
        setRange({ from, to: clickedDate.toDate() })
      }
      return
    }

    setRange({
      from: clickedDate.toDate(),
      to: selectFullMonthRange ? clickedDate.endOf('month').toDate() : undefined
    })
  }

  return slicedMonthNames.map((monthName, index) => {
    const year = monthYear.getFullYear()
    const date = dayjs().year(year).month(index).startOf('month')

    const isRangeStart = Boolean(
      highlightRange.from && date.isSame(highlightRange.from, 'month')
    )
    const isRangeEnd = Boolean(
      highlightRange.to && date.isSame(highlightRange.to, 'month')
    )
    const hasFullRange = Boolean(highlightRange.from && highlightRange.to)

    const isSelected =
      isRangeStart ||
      isRangeEnd ||
      (selectedMonth && date.isSame(selectedMonth, 'month'))

    const isMonthInRange =
      highlightRange.from &&
      highlightRange.to &&
      date.isAfter(highlightRange.from, 'month') &&
      date.isBefore(highlightRange.to, 'month')

    // серверное время, а не часы браузера: остальной пикер живёт по нему же
    const isCurrentMonth = getMskNow().isSame(date, 'month')

    const isDisabledByBounds = disabled
      ? Boolean(
          (disabled.before && date.isBefore(disabled.before, 'month')) ||
            (disabled.after && date.isAfter(disabled.after, 'month'))
        )
      : date.isAfter(dayjs(), 'month')

    return (
      <button
        key={index}
        type="button"
        data-testid={`month-picker-${index}-id`}
        disabled={isDisabledByBounds}
        className={cn(
          'h-50 w-93 rounded-6',
          'capitalize text-16',
          'hover:bg-bronze-40 hover:text-white',
          isSelected && 'bg-bronze-40 text-white !text-16',
          hasFullRange && isRangeStart && !isRangeEnd && 'rounded-e-0',
          hasFullRange && isRangeEnd && !isRangeStart && 'rounded-s-0',
          isCurrentMonth && !isSelected && 'text-bronze-40 !text-16',
          isMonthInRange && '!rounded-0 bg-grey-10',
          isDisabledByBounds &&
            'cursor-default text-grey-70 !text-16 disabled:pointer-events-none',
          cellClassName
        )}
        onClick={() => handleMonthClick(index)}
      >
        {monthName}
      </button>
    )
  })
}
